/**
 * Task status and priority semantics, with no dependency on the tRPC router types.
 *
 * Split out of `task-list.ts` so the backend can resolve terminality and priority
 * through the same code the portals use. `task-list.ts` derives its types from
 * `RouterOutputs`, which reaches `lib/trpc.ts` — a browser module referencing
 * `window` and `import.meta.env`. Importing any of it from a Worker drags those
 * into the backend's type program, and it is circular besides: the router types
 * come from the backend in the first place.
 *
 * These functions take a structural shape rather than the router type, so the
 * portals keep passing `TaskStatusMeta[]` unchanged. `task-list.ts` re-exports
 * everything here, and remains the module callers name — see rule 8 in
 * `.agents/rules/coding_standards.md` and `scripts/assert-entity-renderers.mjs`.
 */

/** The part of a workspace status that decides behaviour, rather than presentation. */
export interface TaskStatusLike {
  key: string;
  isTerminal: boolean;
}

export interface TaskPriorityMeta {
  /** Stored priority value (matches the create/edit dialogs — the only writers). */
  value: number;
  label: 'Low' | 'Medium' | 'High';
  /** Badge/pill classes for this level. */
  className: string;
  /** Text-only class for group headers / minimal contexts. */
  textClass: string;
}

/**
 * The single source of truth for task priority, keyed to what the create/edit
 * dialogs actually store: 0 = Low, 1 = Medium, 2 = High. Highest-first so it can
 * drive ordered UI (filter lists, group headers). Everything that renders or
 * groups priority MUST go through here — do not re-derive a P1/P2/P3 scale, which
 * is what previously drifted out of sync with stored data.
 */
export const TASK_PRIORITY_OPTIONS: readonly TaskPriorityMeta[] = [
  {
    value: 2,
    label: 'High',
    className: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    textClass: 'text-red-500 dark:text-red-400',
  },
  {
    value: 1,
    label: 'Medium',
    className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    textClass: 'text-amber-500 dark:text-amber-400',
  },
  {
    value: 0,
    label: 'Low',
    className: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
    textClass: 'text-slate-500 dark:text-slate-400',
  },
] as const;

/** Priority meta for a stored value (null/absent → Low). */
export function getPriorityMeta(priority: number | null): TaskPriorityMeta {
  return TASK_PRIORITY_OPTIONS.find((p) => p.value === (priority ?? 0)) ?? TASK_PRIORITY_OPTIONS[2];
}

/**
 * Priority meta only when it's worth surfacing a badge — Medium/High. Low/None
 * return undefined so compact surfaces (cards, list rows) stay uncluttered while
 * still using the same source of truth.
 */
export function getElevatedPriorityMeta(priority: number | null): TaskPriorityMeta | undefined {
  return (priority ?? 0) > 0 ? getPriorityMeta(priority) : undefined;
}

/**
 * Whether a status means the task is finished.
 *
 * Statuses are workspace-configurable rows, so this is the only way to ask. An
 * unknown status is not terminal: a workspace that just added a status should not
 * have its open tasks silently treated as done.
 */
export function isTerminalStatus(statuses: TaskStatusLike[], statusKey: string | null): boolean {
  return statuses.find((s) => s.key === statusKey)?.isTerminal ?? false;
}
